<template>
  <section class="notes-section" :class="ui.section" :aria-labelledby="headingId">
    <header class="notes-section-header">
      <h3 :id="headingId" class="notes-section-heading">
        <i :class="ui.icon" aria-hidden="true"></i>
        {{ title }} <small>({{ notes.length }})</small>
      </h3>
      <button
        v-if="collapsible"
        type="button"
        class="btn-icon section-collapse-toggle"
        :aria-expanded="expanded.toString()"
        :aria-controls="contentId"
        :aria-label="collapseToggleLabel"
        @click="expanded = !expanded"
      >
        <i :class="`fas fa-angle-${expanded ? 'up' : 'down'}`" aria-hidden="true"></i>
      </button>
    </header>

    <div v-show="expanded" :id="contentId" class="notes-section-content">
      <empty-text-placeholder v-if="!notes.length" :empty-text="emptyText"></empty-text-placeholder>
      <ol v-else :id="listId" class="notes-list" role="list">
        <li
          v-for="note in visiblenotes"
          :key="note.id"
          class="notes-item panel panel-default"
          :class="ui.item"
        >
          <div class="notes-item-header">
            <router-link
              :class="ui.link"
              :to="{ name: 'module_meeting', params: { moduleId: note.module_id, moduleMeetingId: note.module_meeting_id } }"
            >
              <span :class="ui.title">
                {{ note.meeting_name }}
              </span>
            </router-link>
            <k-status-pill
              v-if="variant === 'actions-required'"
              class="actions-required-pill"
              status-value="signature required"
              type="warning-alpha"
            />
          </div>
          <span class="notes-item-date"><i class="fa-duotone fa-calendar" aria-hidden="true"></i> {{ parseTimestamp(note.meeting_start) }}</span>
        </li>
      </ol>

      <button
        v-if="notes.length > compactLimit"
        type="button"
        class="btn-link section-toggle"
        :aria-expanded="showAll.toString()"
        :aria-controls="listId"
        :aria-label="showAllToggleLabel"
        @click="showAll = !showAll"
      >
        {{ toggleText }}
        <i :class="`fas fa-angle-${showAll ? 'up' : 'right'}`" aria-hidden="true"></i>
      </button>
    </div>
  </section>
</template>

<style scoped>
.notes-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 15px 0;
}

.notes-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.notes-section-header button:hover {
  color: var(--kate-type-link-hover);
}

.notes-section-header button:focus-visible,
.section-toggle:focus-visible,
.notes-item a:focus-visible {
  outline: 2px solid currentcolor;
  outline-offset: 2px;
}

.notes-section-heading small {
  color: var(--kate-type-alt);
  font-size: 0.85em;
}

.notes-section-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notes-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.notes-item {
  margin-bottom: 0;
}

.section-toggle {
  align-items: center;
  justify-content: center;
  color: var(--kate-type-link);
  text-decoration: none;
  display: flex;
  gap: 5px;
  width: 100%;
}

.section-toggle:hover {
  text-decoration: underline;
}

.section-toggle:focus {
  color: var(--kate-type-link-hover);
}

.section-collapse-toggle[aria-expanded="true"] {
  width: unset;
}

.notes-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.actions-required-pill {
  font-size: 0.75rem;
}

.actions-required-item {
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid var(--kate-warning);
  background-color: color-mix(in srgb, var(--kate-warning) 12%, transparent);
}

.actions-required-item-link {
  width: 60%;
  color: var(--kate-type-warning);
}

.actions-required-item-title {
  color: var(--kate-type-warning);
  text-decoration: underline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.actions-required-item-title:hover,
.actions-required-item-link:focus .actions-required-item-title {
  color: var(--kate-type-link-hover);
}

.no-action-item {
  border-radius: 8px;
  padding: 0 10px;
  background-color: transparent;
  box-shadow: none;
}

.no-action-item-link {
  width: 100%;
}

.no-action-item-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

[data-theme="light"] .actions-required-pill,
[data-theme="light"] .actions-required-item-title {
  color: #754600;
}

[data-theme="light"] .no-action-item-title:hover,
[data-theme="light"] .no-action-item-link:focus .no-action-item-title,
[data-theme="light"] .actions-required-item-link:hover .actions-required-item-title,
[data-theme="light"] .actions-required-item-link:focus .actions-required-item-title {
  color: var(--kate-type-link-hover);
}
</style>

<script>
import KStatusPill from '@base-components/k-status-pill.vue';
import TimeMixin from '@mixins/time-mixins';
import EmptyTextPlaceholder from '../components/empty-text-placeholder.vue';

const VARIANT_UI = {
  'actions-required': {
    section: 'actions-section',
    item: 'actions-required-item',
    link: 'actions-required-item-link',
    title: 'actions-required-item-title',
    icon: 'far fa-exclamation-circle',
  },
  'mentor-sessions': {
    section: 'mentor-section',
    item: 'no-action-item',
    link: 'no-action-item-link',
    title: 'no-action-item-title',
    icon: 'far fa-star',
  },
  'other-notes': {
    section: 'other-section',
    item: 'no-action-item',
    link: 'no-action-item-link',
    title: 'no-action-item-title',
    icon: 'far fa-note',
  },
};

export default {
  components: {
    KStatusPill,
    EmptyTextPlaceholder,
  },
  mixins: [TimeMixin],
  props: {
    title: {
      type: String,
      required: true,
    },
    notes: {
      type: Array,
      default: () => [],
    },
    compactLimit: {
      type: Number,
      default: 2,
    },
    variant: {
      type: String,
      default: 'other-notes',
      validator: value => ['actions-required', 'mentor-sessions', 'other-notes'].includes(value),
    },
    emptyText: {
      type: String,
      default: 'No notes in this section.',
    },
    collapsible: {
      type: Boolean,
      default: false,
    },
    initiallyExpanded: {
      type: Boolean,
      default: true,
    },
  },

  beforeMount() {
    this.uuid = this.$.uid;
  },

  data() {
    return {
      expanded: this.initiallyExpanded,
      showAll: false,
    };
  },

  computed: {
    ui() {
      return VARIANT_UI[this.variant] || VARIANT_UI['other-notes'];
    },

    headingId() {
      return `meeting-notes-section-${this.uuid}-heading`;
    },

    contentId() {
      return `meeting-notes-section-${this.uuid}-content`;
    },

    listId() {
      return `meeting-notes-section-${this.uuid}-list`;
    },

    collapseToggleLabel() {
      return this.expanded ? `Hide ${this.title} notes` : `Show ${this.title} notes`;
    },

    showAllToggleLabel() {
      return `${this.toggleText} ${this.title} notes`;
    },

    visiblenotes() {
      return this.showAll ? this.notes : this.notes.slice(0, this.compactLimit);
    },

    toggleText() {
      if (this.showAll) {
        return 'View less';
      }
      const hidden = Math.max(this.notes.length - this.compactLimit, 0);
      return `View ${hidden} more`;
    },
  },

};
</script>
